/**
 * Glycaemic conversions and indices. CLAUDE.md §2.4.
 *
 * Every function here is pure and total: no I/O, no clock, no randomness, no
 * framework. Each carries its source, its valid input range, and an explicit
 * behaviour on out-of-range or missing input (§2.4).
 */
import { convertGlucose, convertHba1c, fail, ok, quantity, } from '@glycemia/units';
/**
 * Estimated average glucose from HbA1c.
 *
 * eAG (mg/dL) = 28.7 × HbA1c(%) − 46.7   — CLAUDE.md §2.4.
 * Source: Nathan DM et al., ADAG study, Diabetes Care 2008;31(8):1473-8.
 * Valid for HbA1c 5.0–12.0% (the range studied). Outside it the linear fit is
 * extrapolation, so this returns OUT_OF_RANGE rather than a plausible number.
 */
export const EAG_SLOPE_MGDL_PER_PERCENT = 28.7;
export const EAG_INTERCEPT_MGDL = 46.7;
export const EAG_VALID_NGSP_MIN = 5.0;
export const EAG_VALID_NGSP_MAX = 12.0;
export const estimatedAverageGlucose = (hba1c) => {
    const ngsp = convertHba1c(hba1c, '%');
    if (!ngsp.ok)
        return ngsp;
    const percent = ngsp.value.value;
    if (percent < EAG_VALID_NGSP_MIN || percent > EAG_VALID_NGSP_MAX) {
        return fail('OUT_OF_RANGE', `eAG is defined for HbA1c ${EAG_VALID_NGSP_MIN}–${EAG_VALID_NGSP_MAX}% (ADAG study range); ` +
            `received ${percent}%. Extrapolating the linear fit outside that range is not supported.`, { hba1cNgspPercent: percent });
    }
    return quantity(EAG_SLOPE_MGDL_PER_PERCENT * percent - EAG_INTERCEPT_MGDL, 'mg/dL');
};
/**
 * Glucose Management Indicator.
 *
 * GMI (%) = 3.31 + 0.02392 × mean glucose (mg/dL)   — CLAUDE.md §2.4.
 * Source: Bergenstal RM et al., Diabetes Care 2018;41(11):2275-2280.
 *
 * GMI is NOT HbA1c and must never be labelled as one in the UI. It is derived
 * from a CGM window and estimates what the laboratory HbA1c would be.
 * Valid for mean glucose roughly 40–400 mg/dL.
 */
export const GMI_INTERCEPT_PERCENT = 3.31;
export const GMI_SLOPE_PERCENT_PER_MGDL = 0.02392;
export const glucoseManagementIndicator = (meanGlucose) => {
    const mgDl = convertGlucose(meanGlucose, 'mg/dL');
    if (!mgDl.ok)
        return mgDl;
    const value = mgDl.value.value;
    if (value < 40 || value > 400) {
        return fail('OUT_OF_RANGE', `GMI is defined for mean glucose 40–400 mg/dL; received ${value} mg/dL.`, { meanGlucoseMgDl: value });
    }
    return quantity(GMI_INTERCEPT_PERCENT + GMI_SLOPE_PERCENT_PER_MGDL * value, '%');
};
/**
 * Coefficient of variation of a glucose series.
 *
 * CV (%) = SD / mean × 100   — CLAUDE.md §2.4. Target ≤36%.
 * Source: Battelino T et al., ATTD consensus, Diabetes Care 2019;42(8):1593-1603.
 *
 * Uses the SAMPLE standard deviation (n−1). A CGM window is a sample of an
 * underlying process, not the whole population, and the ATTD consensus reports
 * sample SD. At least two readings are required.
 */
export const coefficientOfVariation = (glucoseValuesMgDl) => {
    if (glucoseValuesMgDl.length < 2) {
        return fail('INSUFFICIENT_DATA', `CV requires at least 2 readings; received ${glucoseValuesMgDl.length}.`, { n: glucoseValuesMgDl.length });
    }
    if (glucoseValuesMgDl.some((v) => !Number.isFinite(v))) {
        return fail('NOT_FINITE', 'Glucose series contains a non-finite value.');
    }
    const n = glucoseValuesMgDl.length;
    const mean = glucoseValuesMgDl.reduce((a, b) => a + b, 0) / n;
    if (mean === 0) {
        return fail('DIVISION_BY_ZERO', 'Mean glucose is zero; CV is undefined.');
    }
    const variance = glucoseValuesMgDl.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (n - 1);
    return quantity((Math.sqrt(variance) / mean) * 100, '%');
};
/** Arithmetic mean of a glucose series in mg/dL. Refuses an empty series (§16.3). */
export const meanGlucoseMgDl = (glucoseValuesMgDl) => {
    if (glucoseValuesMgDl.length === 0) {
        return fail('INSUFFICIENT_DATA', 'Cannot take the mean of an empty glucose series.');
    }
    if (glucoseValuesMgDl.some((v) => !Number.isFinite(v))) {
        return fail('NOT_FINITE', 'Glucose series contains a non-finite value.');
    }
    return quantity(glucoseValuesMgDl.reduce((a, b) => a + b, 0) / glucoseValuesMgDl.length, 'mg/dL');
};
/**
 * Sample standard deviation of a glucose series, in mg/dL.
 * Exposed because AGP reports show SD alongside CV.
 */
export const standardDeviationMgDl = (glucoseValuesMgDl) => {
    if (glucoseValuesMgDl.length < 2) {
        return fail('INSUFFICIENT_DATA', `SD requires at least 2 readings; received ${glucoseValuesMgDl.length}.`);
    }
    const meanResult = meanGlucoseMgDl(glucoseValuesMgDl);
    if (!meanResult.ok)
        return meanResult;
    const mean = meanResult.value.value;
    const n = glucoseValuesMgDl.length;
    const variance = glucoseValuesMgDl.reduce((acc, v) => acc + (v - mean) ** 2, 0) / (n - 1);
    return ok({ value: Math.sqrt(variance), unit: 'mg/dL' });
};
//# sourceMappingURL=glycemia.js.map